// src/pages/FacilitiesPage.js
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import axiosInstance from "../api/axiosInstance";

const letters = ["All", ...Array.from({ length: 26 }, (_, i) => String.fromCharCode(65 + i))];

const FacilitiesPage = () => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(12);
  const [startsWith, setStartsWith] = useState("All");

  // Split initial load vs refetch (smooth UX)
  const [initialLoading, setInitialLoading] = useState(true);
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState(null);

  // Cancel in-flight requests when user clicks quickly
  const abortRef = useRef(null);
  const hasLoadedOnceRef = useRef(false);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / pageSize)), [total, pageSize]);

  const fetchHotels = useCallback(async () => {
    // Cancel previous request (prevents flicker / race conditions)
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    // Show full loader only once (first mount)
    if (!hasLoadedOnceRef.current) setInitialLoading(true);
    setFetching(true);
    setError(null);

    try {
      const params = { page, pageSize };
      if (startsWith && startsWith !== "All") params.startsWith = startsWith;

      const res = await axiosInstance.get("/Hotel", {
        params,
        signal: controller.signal,
      });

      // Expecting { items, total, page, pageSize }
      const data = res?.data || {};
      setItems(Array.isArray(data.items) ? data.items : []);
      setTotal(Number.isFinite(data.total) ? data.total : 0);

      // Keep server values if returned
      if (Number.isFinite(data.page)) setPage(data.page);
      if (Number.isFinite(data.pageSize)) setPageSize(data.pageSize);

      hasLoadedOnceRef.current = true;
    } catch (err) {
      // Ignore abort errors (user clicked another filter)
      if (err?.name === "CanceledError" || err?.name === "AbortError") return;

      console.error("Error fetching hotels:", err);
      setError("Failed to load hotel data.");
      // Keep old items on screen; just show the error
    } finally {
      setFetching(false);
      setInitialLoading(false);
    }
  }, [page, pageSize, startsWith]);

  useEffect(() => {
    fetchHotels();
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, [fetchHotels]);

  const onSelectLetter = (l) => {
    setStartsWith(l);
    setPage(1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const hasOwner = (hotel) =>
    (hotel?.ownerName && String(hotel.ownerName).trim()) ||
    (Array.isArray(hotel?.owners) && hotel.owners.length > 0);

  const getPrimaryOwner = (hotel) => {
    if (hotel?.ownerName || hotel?.ownerEmail || hotel?.ownerPhone) {
      return {
        fullName: hotel.ownerName,
        email: hotel.ownerEmail,
        phoneNumber: hotel.ownerPhone,
      };
    }
    return Array.isArray(hotel?.owners) && hotel.owners[0] ? hotel.owners[0] : null;
  };

  const Sidebar = ({ disabled }) => (
    <aside
      style={{
        position: "sticky",
        top: 16,
        width: 84,
        border: "1px solid rgba(0,0,0,.12)",
        borderRadius: 14,
        padding: 10,
        background: "linear-gradient(180deg, #0f0b08, #1a120d)",
        boxShadow: "0 10px 26px rgba(0,0,0,.12)",
      }}
    >
      <div
        style={{
          fontWeight: 900,
          fontSize: 11,
          marginBottom: 10,
          letterSpacing: ".18em",
          textTransform: "uppercase",
          color: "rgba(255,255,255,.78)",
        }}
      >
        Filter
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
        {letters.map((l) => {
          const active = l === startsWith;

          return (
            <button
              key={l}
              onClick={() => onSelectLetter(l)}
              disabled={disabled || (fetching && active)}
              style={{
                cursor: disabled ? "not-allowed" : "pointer",
                borderRadius: 12,
                border: active
                  ? "1px solid rgba(230, 207, 157, .65)"
                  : "1px solid rgba(255,255,255,.14)",
                background: active
                  ? "linear-gradient(135deg, #b89b6e, #e6cf9d)"
                  : "rgba(255,255,255,.08)",
                color: active ? "#25170a" : "rgba(255,255,255,.92)",
                padding: "7px 8px",
                fontWeight: active ? 900 : 700,
                letterSpacing: ".04em",
                opacity: disabled ? 0.55 : fetching && !active ? 0.9 : 1,
                transition: "opacity 160ms ease, transform 120ms ease",
              }}
            >
              {l}
            </button>
          );
        })}
      </div>
    </aside>
  );

  const PagingBar = () => (
    <div
      style={{
        display: "flex",
        gap: 10,
        alignItems: "center",
        justifyContent: "flex-end",
        flexWrap: "wrap",
        borderTop: "1px solid rgba(0,0,0,.08)",
        paddingTop: 12,
        marginTop: 16,
      }}
    >
      <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <span style={{ opacity: 0.75 }}>Page size</span>
        <select
          value={pageSize}
          onChange={(e) => {
            setPageSize(Number(e.target.value));
            setPage(1);
          }}
          disabled={fetching}
          style={{
            borderRadius: 10,
            padding: "6px 10px",
            border: "1px solid rgba(0,0,0,.12)",
            background: "#fff",
          }}
        >
          {[6, 12, 24, 48].map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
      </label>

      <button
        onClick={() => setPage((p) => Math.max(1, p - 1))}
        disabled={page <= 1 || fetching}
        style={{
          borderRadius: 10,
          padding: "7px 12px",
          border: "1px solid rgba(0,0,0,.12)",
          background: "#fff",
          cursor: page <= 1 || fetching ? "not-allowed" : "pointer",
          opacity: page <= 1 || fetching ? 0.6 : 1,
        }}
      >
        Prev
      </button>

      <span style={{ minWidth: 140, textAlign: "center", opacity: 0.9 }}>
        Page <strong>{page}</strong> / <strong>{totalPages}</strong>
      </span>

      <button
        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
        disabled={page >= totalPages || fetching}
        style={{
          borderRadius: 10,
          padding: "7px 12px",
          border: "1px solid rgba(0,0,0,.12)",
          background: "#fff",
          cursor: page >= totalPages || fetching ? "not-allowed" : "pointer",
          opacity: page >= totalPages || fetching ? 0.6 : 1,
        }}
      >
        Next
      </button>

      <button
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        disabled={fetching}
        style={{
          borderRadius: 10,
          padding: "7px 12px",
          border: "1px solid rgba(0,0,0,.12)",
          background: "#fff",
          cursor: fetching ? "not-allowed" : "pointer",
          opacity: fetching ? 0.6 : 0.9,
        }}
      >
        Top
      </button>
    </div>
  );

  // First-load skeleton (keeps layout; does not “reload” later)
  if (initialLoading) {
    return (
      <div style={{ display: "flex", gap: 18, alignItems: "flex-start" }}>
        <Sidebar disabled />

        <main style={{ flex: 1 }}>
          <h2 style={{ margin: 0 }}>Registered Facilities</h2>
          <div style={{ opacity: 0.7, marginTop: 6 }}>Loading…</div>

          <div style={{ marginTop: 16, display: "grid", gap: 12 }}>
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                style={{
                  height: 120,
                  borderRadius: 14,
                  border: "1px solid rgba(0,0,0,.08)",
                  background:
                    "linear-gradient(90deg, rgba(0,0,0,.03), rgba(0,0,0,.06), rgba(0,0,0,.03))",
                  backgroundSize: "200% 100%",
                  animation: "shimmer 1.1s infinite linear",
                }}
              />
            ))}
          </div>

          <style>{`
            @keyframes shimmer { 
              0% { background-position: 200% 0; } 
              100% { background-position: -200% 0; } 
            }
          `}</style>
        </main>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", gap: 18, alignItems: "flex-start" }}>
      {/* A-Z Sidebar */}
      <Sidebar disabled={false} />

      {/* Content */}
      <main style={{ flex: 1 }}>
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12 }}>
          <div>
            <h2 style={{ margin: 0 }}>Registered Facilities</h2>
            <div style={{ opacity: 0.7, marginTop: 6 }}>
              Showing <strong>{items.length}</strong> of <strong>{total}</strong>{" "}
              {startsWith === "All" ? "hotels" : `hotels starting with "${startsWith}"`}
              {fetching ? <span style={{ marginLeft: 10 }}>• Updating…</span> : null}
            </div>
          </div>
        </div>

        {error ? (
          <div
            style={{
              marginTop: 12,
              padding: "10px 12px",
              borderRadius: 12,
              border: "1px solid rgba(190,40,40,.25)",
              background: "rgba(190,40,40,.06)",
              color: "#7b1a1a",
            }}
          >
            {error}
          </div>
        ) : null}

        {/* Keep list visible; just fade during fetch */}
        <ul
          style={{
            listStyle: "none",
            padding: 0,
            marginTop: 16,
            opacity: fetching ? 0.65 : 1,
            transition: "opacity 160ms ease",
          }}
          aria-busy={fetching ? "true" : "false"}
        >
          {items.length > 0 ? (
            items.map((hotel) => {
              const primaryOwner = getPrimaryOwner(hotel);
              const ownerOk = hasOwner(hotel) && primaryOwner;

              return (
                <li
                  key={String(hotel.hotelId ?? hotel.id ?? Math.random())}
                  style={{
                    border: "1px solid rgba(0,0,0,.08)",
                    borderRadius: 14,
                    padding: 14,
                    marginBottom: 12,
                    background: "#fff",
                  }}
                >
                  <h3 style={{ marginTop: 0 }}>{hotel.hotelName}</h3>

                  <p>📍 Address: {hotel.address || "-"}</p>
                  <p>📞 Contact: {hotel.contactNumber || "-"}</p>

                  <p>
                    🌐{" "}
                    {hotel.website ? (
                      <a href={hotel.website} target="_blank" rel="noopener noreferrer">
                        {hotel.website}
                      </a>
                    ) : (
                      "-"
                    )}
                  </p>

                  <p>🏨 Type: {hotel.type || "-"}</p>

                  {Array.isArray(hotel.amenities) && hotel.amenities.length > 0 && (
                    <p>🛎️ Amenities: {hotel.amenities.join(", ")}</p>
                  )}

                  <p>
                    👤 Owner:{" "}
                    {ownerOk ? (
                      <>
                        <strong>{primaryOwner.fullName || "Owner"}</strong>
                        {primaryOwner.email ? (
                          <>
                            {" "}
                            • <a href={`mailto:${primaryOwner.email}`}>{primaryOwner.email}</a>
                          </>
                        ) : null}
                        {primaryOwner.phoneNumber ? <> • {primaryOwner.phoneNumber}</> : null}
                      </>
                    ) : (
                      <span style={{ opacity: 0.7 }}>Not assigned</span>
                    )}
                  </p>

                  {Array.isArray(hotel.owners) && hotel.owners.length > 1 && (
                    <p>
                      👥 Co-owners:{" "}
                      {hotel.owners
                        .slice(1)
                        .map((o) => o.fullName || o.email || `User ${o.userId}`)
                        .join(", ")}
                    </p>
                  )}
                </li>
              );
            })
          ) : (
            <p>No hotels available.</p>
          )}
        </ul>

        {/* Paging ONLY at the bottom */}
        <PagingBar />
      </main>
    </div>
  );
};

export default FacilitiesPage;
